"use client";

import React, { FunctionComponent, useEffect, useState } from "react";
import {
  IPricingFields,
  IPricingPlan,
  IPricingPlanFields,
} from "@/types/generated/contentful";
import CallToAction from "../callToAction";
import { AnimatePresence, motion } from "framer-motion";

type Props = {
  content: IPricingPlan;
};

const PricingPlan: FunctionComponent<Props> = ({ content }) => {
  const {
    cta,
    defaultPrice,
    features,
    featuresHeading,
    isPopular,
    prices,
    title,
  } = content.fields as IPricingPlanFields;
  const defaultPricing = defaultPrice?.fields as IPricingFields;

  const [selectedPricing, setSelectedPricing] = useState<
    IPricingFields | undefined
  >(defaultPricing);
  const [isChecked, setIsChecked] = useState(true);

  const borderStyle = isPopular
    ? "border-2 border-primary"
    : "border border-gray-300";

  const getBillingCycleShortForm = (cycle: IPricingFields["billingCycle"]) => {
    switch (cycle) {
      case "annually":
        return "year";
      case "monthly":
        return "month";
      case "quarterly":
        return "quarter";
    }
  };

  const getBillingCycle = (cycle: IPricingFields["billingCycle"]) => {
    switch (cycle) {
      case "annually":
        return "Annual";
      case "monthly":
        return "Monthly";
      case "quarterly":
        return "Quarterly";
    }
  };

  const priceToggle = () => {
    if (!prices) return <></>;

    const otherPrices = prices[0].fields as IPricingFields;

    return (
      <div className="mt-2 flex h-full w-full xl:mt-0">
        <label className="inline-flex flex-grow cursor-pointer items-center justify-center">
          <span className="mr-3 text-sm font-medium">
            {getBillingCycle(otherPrices.billingCycle)}
          </span>
          <input
            type="checkbox"
            className="peer sr-only"
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
          />
          <div className="peer relative h-6 w-11 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-gray-200 peer-checked:after:translate-x-full peer-checked:after:border-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rtl:peer-checked:after:-translate-x-full"></div>
          <span className="ms-3 whitespace-nowrap text-sm font-medium">
            {getBillingCycle(defaultPricing.billingCycle)}
          </span>
        </label>
      </div>
    );
  };

  useEffect(() => {
    if (isChecked) {
      setSelectedPricing(defaultPricing);
    } else {
      prices && setSelectedPricing(prices[0].fields as IPricingFields);
    }
  }, [defaultPricing, isChecked, prices, setSelectedPricing]);

  return (
    <div className="flex h-full flex-col">
      {isPopular ? (
        <div className="rounded-t-xl bg-primary p-2 text-center text-xl font-bold text-white">
          Most popular
        </div>
      ) : (
        <div className="h-11"></div>
      )}
      <div
        className={`flex h-100 flex-col gap-3 bg-white p-4 ${isPopular ? "rounded-b-xl" : "rounded-xl"} ${borderStyle}`}
      >
        <div className="flex flex-grow flex-col gap-4">
          <h3 className="text-center">{title}</h3>
          {defaultPricing ? (
            <div className="flex flex-col items-start gap-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedPricing?.price} // re-animate on price change
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  transition={{ duration: 0.3 }}
                  className="flex w-full flex-row justify-center"
                >
                  <span className="text-3xl font-medium lg:text-4xl">
                    ${selectedPricing?.price}
                  </span>
                  {selectedPricing?.billingCycle && (
                    <div className="flex h-full items-end">
                      <span className="flex-none whitespace-nowrap">
                        /{" "}
                        {getBillingCycleShortForm(selectedPricing.billingCycle)}
                      </span>
                    </div>
                  )}
                  {selectedPricing?.savings && (
                    <div className="ml-2 flex h-full items-center">
                      <span className="rounded-full border border-green-800 bg-green-100 p-2 text-xs font-bold text-green-800">
                        Save {selectedPricing.savings}
                      </span>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
              {priceToggle()}
            </div>
          ) : (
            <span className="flex h-10 w-full items-center justify-center text-2xl font-medium">
              Custom
            </span>
          )}
        </div>
        <CallToAction content={cta} appendClass="md:!w-full mt-2" />
        <div className="text-md mt-2 h-36 lg:text-base">
          <span>{featuresHeading}</span>
          <ul className="list-disc">
            {features.map((feature, index) => (
              <li key={index} className="ml-5">
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PricingPlan;
